import React from 'react';
import { Gauge, ArrowLeft, Shield } from 'lucide-react';
import { Markdown } from '@com.yanicksenn/shared';
import { useI18n } from '@/context/I18nContext';
import { useRouter } from '@/context/RouterContext';
import { Footer } from '@/components/navigation/Footer';
import { LanguageDropdown } from '@shared/auth/LanguageDropdown';
import type { Locale } from '@/i18n';
import privacyEnMarkdown from '@resources/privacy_policy.en.md';
import privacyDeMarkdown from '@resources/privacy_policy.de.md';
import privacyFrMarkdown from '@resources/privacy_policy.fr.md';
import privacyEsMarkdown from '@resources/privacy_policy.es.md';
import privacyItMarkdown from '@resources/privacy_policy.it.md';
import privacyNlMarkdown from '@resources/privacy_policy.nl.md';

const PRIVACY_MARKDOWN_BY_LOCALE: Record<Locale, string> = {
  en: privacyEnMarkdown,
  de: privacyDeMarkdown,
  fr: privacyFrMarkdown,
  es: privacyEsMarkdown,
  it: privacyItMarkdown,
  nl: privacyNlMarkdown,
};

export const PrivacyPolicyPage: React.FC = () => {
  const { t, locale, setLocale } = useI18n();
  const { navigate } = useRouter();

  const markdownContent = PRIVACY_MARKDOWN_BY_LOCALE[locale] ?? privacyEnMarkdown;

  return (
    <div className="min-h-screen flex flex-col bg-bg-base text-text-primary">
      <header className="nav-bar h-[60px] sm:h-[52px] sticky top-0 z-40">
        <div className="h-full max-w-[960px] mx-auto px-5 sm:px-8 flex items-center justify-between">
          <a
            href="#/"
            onClick={(e) => {
              e.preventDefault();
              navigate('#/');
            }}
            className="flex items-center gap-2.5 text-text-primary hover:opacity-90 transition-opacity"
          >
            <Gauge size={18} className="text-accent" />
            <span className="text-sm font-semibold tracking-tight">kart.win</span>
          </a>

          <div className="flex items-center gap-2">
            <LanguageDropdown
              locale={locale}
              onChange={setLocale}
              testIdPrefix="privacy-page-locale"
            />
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-[820px] mx-auto px-5 sm:px-8 pt-8 pb-12">
        <a
          href="#/"
          onClick={(e) => {
            e.preventDefault();
            navigate('#/');
          }}
          data-testid="privacy-page-back"
          className="inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-4 cursor-pointer"
        >
          <ArrowLeft size={14} />
          <span>{t('common.back')}</span>
        </a>

        <div className="card p-6 sm:p-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-medium mb-4">
            <Shield size={13} />
            <span>{t('footer.privacyPolicy')}</span>
          </div>

          <Markdown content={markdownContent} />
        </div>
      </main>

      <Footer />
    </div>
  );
};
